import { Outlet, useLocation, useNavigate } from 'react-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { BottomTabBar } from '@/roles/sv/shell/BottomTabBar'
import { Drawer } from '@/roles/sv/shell/Drawer'
import { Rail, pageTitleFor } from '@/roles/sv/shell/Rail'
import { HostBackButton } from '@/shared/shell/HostBackButton'
import { TopBar } from '@/roles/sv/shell/TopBar'
import { usePageEnter } from '@/shared/shell/usePageEnter'
import { omsTitleForPath, type OmsLocale } from '@/features/oms/nav'
import { navKeyFromPath, type NavKey } from '@/roles/sv/nav'
import { useLayoutStore } from '@/shared/store/layoutStore'
import { useAuthStore } from '@/shared/store/authStore'
import { AiButton } from '@/features/ai/AiButton'
import { OmsFrame, type OmsFrameCommand } from '@/features/oms/OmsFrame'
import type { OmsOpenOptions } from '@/features/oms/bridge'

function activeNavKeyForLocation(pathname: string, search: string): NavKey {
  if (pathname.startsWith('/insp/') && new URLSearchParams(search).get('from') === 'hist') {
    return 'hist'
  }
  return navKeyFromPath(pathname)
}

const ROLE_LABEL = 'SV / 슈퍼바이저'

export default function SvShell() {
  const location = useLocation()
  const stackTitle = useLayoutStore((state) => state.stackTitle)
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const active = activeNavKeyForLocation(location.pathname, location.search)
  const [omsOptions, setOmsOptions] = useState<OmsOpenOptions | null>(null)
  const [omsTitle, setOmsTitle] = useState<string | null>(null)
  const [omsPath, setOmsPath] = useState('/')
  const [omsCommand, setOmsCommand] = useState<OmsFrameCommand | null>(null)
  const [omsModalOpen, setOmsModalOpen] = useState(false)
  const [omsBadges, setOmsBadges] = useState<Record<string, number>>({})
  const [omsLocale, setOmsLocale] = useState<OmsLocale>('ko')
  const qscReturnPathRef = useRef('/home')
  const mainRef = useRef<HTMLElement>(null)
  usePageEnter(mainRef, location.pathname)
  const omsAppTitle =
    omsLocale === 'en' ? 'Order Management' : omsLocale === 'zh-TW' ? '訂單管理' : '주문 관리'
  const serviceTitle = (
    {
      '/more': 'MY',
      '/comm': '소통',
      '/qsc/home': 'QSC · 매장 품질관리',
      '/prm/home': 'PRM · 매장 운영관리',
      '/qa/home': 'QA · 납품업체 품질관리',
    } as Record<string, string>
  )[location.pathname]
  const title = omsOptions ? omsAppTitle : (serviceTitle ?? pageTitleFor(active, location.search))
  const documentTitle = omsOptions
    ? `QSC: ${omsAppTitle}${omsTitle ? ` - ${omsTitle}` : ''}`
    : `bizbee QSC - ${title}`
  const closeOmsFrame = useCallback(() => {
    if (omsModalOpen) {
      setOmsCommand({ id: Date.now(), type: 'CLOSE_MODAL' })
      return
    }
    const returnPath = qscReturnPathRef.current
    const shouldRestoreQscPath = Boolean(omsOptions)
    setOmsOptions(null)
    setOmsTitle(null)
    setOmsPath('/')
    setOmsCommand(null)
    setOmsModalOpen(false)
    setOmsBadges({})
    if (shouldRestoreQscPath && `${location.pathname}${location.search}` !== returnPath) {
      navigate(returnPath, { replace: true })
    }
  }, [location.pathname, location.search, navigate, omsModalOpen, omsOptions])
  const navigateOmsFrame = useCallback(
    (path: string, menuTitle: string) => {
      if (omsModalOpen) {
        setOmsCommand({ id: Date.now(), type: 'CLOSE_MODAL' })
        return
      }
      setOmsPath(path)
      setOmsTitle(menuTitle)
      setOmsCommand({ id: Date.now(), type: 'NAVIGATE', path })
    },
    [omsModalOpen],
  )
  const syncOmsFrameLocation = useCallback(
    (path: string, nextTitle?: string) => {
      setOmsPath(path)
      setOmsTitle(nextTitle ?? omsTitleForPath(path, omsLocale) ?? null)
    },
    [omsLocale],
  )

  useEffect(() => {
    const listener = (event: Event) => {
      const options = (event as CustomEvent<OmsOpenOptions>).detail ?? {}
      qscReturnPathRef.current = `${location.pathname}${location.search}`
      setOmsOptions(options)
      const nextPath = options.path ?? '/'
      setOmsPath(nextPath)
      setOmsTitle(options.title ?? options.menuName ?? omsTitleForPath(nextPath, omsLocale) ?? null)
      setOmsCommand(null)
      setOmsModalOpen(false)
      setOmsBadges({})
    }
    window.addEventListener('qsc:open-oms', listener)
    return () => window.removeEventListener('qsc:open-oms', listener)
  }, [location.pathname, location.search, omsLocale])

  useEffect(() => {
    setOmsOptions(null)
    setOmsTitle(null)
    setOmsPath('/')
    setOmsCommand(null)
    setOmsModalOpen(false)
    setOmsBadges({})
  }, [location.pathname, location.search])

  const handleOmsBadgeChange = useCallback((key: string, count: number) => {
    setOmsBadges((prev) => ({
      ...prev,
      [key]: count,
    }))
  }, [])

  const omsShellMode = omsOptions
    ? {
        activePath: omsPath,
        badges: omsBadges,
        locale: omsLocale,
        onClose: closeOmsFrame,
        onNavigate: navigateOmsFrame,
      }
    : undefined

  return (
    <>
      <Helmet>
        <title>{documentTitle}</title>
      </Helmet>
      <div className="app-viewport app-safe-shell flex overflow-hidden bg-surface">
        <Rail active={active} onNavigate={closeOmsFrame} omsMode={omsShellMode} />
        <Drawer active={active} onNavigate={closeOmsFrame} />
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <TopBar title={title} subtitle={ROLE_LABEL} />
          <header
            data-qsc-chrome="true"
            className="hidden h-14 shrink-0 items-center justify-between border-b border-border-subtle bg-surface px-7 lg:flex"
          >
            <div className="flex items-center gap-3">
              <HostBackButton />
              <h1 className="text-preset-body-strong text-text">{stackTitle ?? title}</h1>
            </div>
            <div className="flex items-center gap-3">
              <AiButton />
              <span className="rounded-full bg-brand-soft px-3 py-1 text-preset-caption font-semibold text-brand">
                {ROLE_LABEL}
              </span>
              <span className="text-preset-caption text-text-muted">
                {user?.userNm ?? '사용자'}
              </span>
            </div>
          </header>
          <main
            ref={mainRef}
            data-qsc-scroll="content"
            // 홈 화면은 흰 배경이라, 끝에서 튕길 때 드러나는 스크롤 영역 배경도 같은 색으로 맞춘다.
            className={`min-h-0 flex-1 overflow-auto ${location.pathname.endsWith('/home') ? 'bg-surface' : 'bg-bg'}`}
          >
            {omsOptions ? (
              <OmsFrame
                options={omsOptions}
                onTitleChange={setOmsTitle}
                onPathChange={syncOmsFrameLocation}
                onModalStateChange={setOmsModalOpen}
                onBadgeChange={handleOmsBadgeChange}
                onLocaleChange={setOmsLocale}
                command={omsCommand}
              />
            ) : (
              <Outlet />
            )}
          </main>
          {/* 채팅방은 입력창이 화면 맨 아래에 붙어야 해서 하단 바를 숨긴다. */}
          {location.pathname.startsWith('/inq/') ? null : (
            <BottomTabBar active={active} onNavigate={closeOmsFrame} omsMode={omsShellMode} />
          )}
        </div>
      </div>
    </>
  )
}
